import { useCallback, useMemo, useState } from 'react';
import moment, { Moment } from 'moment-timezone';

import {
  DATE_RANGE_CUSTOM,
  DEFAULT_FILTER_SELECTIONS,
  decodeCustomRange,
  decodeFilterValue
} from '../registry/filters';
import { IntelligenceFilterId, IntelligenceFilters } from '../types';

export const ISO_DATE_FORMAT = 'YYYY-MM-DD';

export interface InsightDateRange {
  start: Moment;
  end: Moment;
  /** Inclusive length in days; the comparison window is the same length immediately before it. */
  days: number;
}

export interface InsightFiltersValue {
  /** Every category, defaults included — what the panel renders. */
  selections: Record<IntelligenceFilterId, string>;
  /**
   * Only the categories that differ from their default — what the data seam keys on, so an
   * untouched panel produces the same cache key as no panel at all.
   */
  filters: IntelligenceFilters;
  dateRange: InsightDateRange;
  comparisonRange: InsightDateRange;
  hasActiveFilters: boolean;
  /**
   * The one category whose selector is showing, or `null`. Held here rather than in eight pieces of
   * component state so "only one selector at a time" is structural: opening a second cannot leave
   * the first open, because there is a single slot.
   */
  openFilterId: IntelligenceFilterId | null;
  toggleFilterOpen: (id: IntelligenceFilterId) => void;
  closeFilter: () => void;
  setFilter: (id: IntelligenceFilterId, value: string) => void;
  resetFilter: (id: IntelligenceFilterId) => void;
  clearAll: () => void;
}

// TEMPORARY (PLA-8815): pinned rather than the real clock. UAT returns `status: 'completed'`,
// `rows: []` for every metric on every real-"today" window — there is no live data for the actual
// current date to test against — so every preset would otherwise resolve to an empty window that
// proves nothing. 2025-06-01..2026-07-31 is the range the test tenant's data is expected to cover;
// "today" pins to the range's own end so every preset (Last 7 days, Last 30 days, ...) counts
// backward from inside it. Remove this pin (revert to `moment()`) when told to; nothing else in
// this file changes when it goes.
export const PINNED_TEST_RANGE_START = '2025-06-01';
export const PINNED_TEST_TODAY = '2026-07-31';

const today = (): Moment => moment(PINNED_TEST_TODAY, ISO_DATE_FORMAT, true);

const endOfToday = (): Moment => today().endOf('day');

// The four presets the Date Range card draws, plus the custom range its `Custom range` row carries.
// Every preset ends today inclusive, which is what the card's own default note states ("Last 30
// days, including today").
const resolveDateRange = (value: string): InsightDateRange => {
  const { optionId } = decodeFilterValue(value);
  const custom = decodeCustomRange(value);

  if (optionId === DATE_RANGE_CUSTOM && custom) {
    const start = moment(custom[0], ISO_DATE_FORMAT, true).startOf('day');
    const end = moment(custom[1], ISO_DATE_FORMAT, true).endOf('day');

    if (start.isValid() && end.isValid()) {
      return { start, end, days: end.diff(start, 'day') + 1 };
    }
  }

  const end = endOfToday();

  switch (optionId) {
    case 'last_7_days':
      return { start: today().subtract(6, 'day').startOf('day'), end, days: 7 };
    case 'current_month': {
      const start = today().startOf('month');

      return { start, end, days: end.diff(start, 'day') + 1 };
    }
    case 'previous_month': {
      const start = today().subtract(1, 'month').startOf('month');
      const monthEnd = today().subtract(1, 'month').endOf('month');

      return { start, end: monthEnd, days: monthEnd.diff(start, 'day') + 1 };
    }
    default:
      return { start: today().subtract(29, 'day').startOf('day'), end, days: 30 };
  }
};

// `containers/Analytics/index.tsx`: the comparison window is the same number of days, ending the day
// before the selected one starts.
const previousRange = ({ start, end, days }: InsightDateRange): InsightDateRange => ({
  start: start.clone().subtract(days, 'day'),
  end: end.clone().subtract(days, 'day'),
  days
});

const activeFiltersOf = (selections: Record<IntelligenceFilterId, string>): IntelligenceFilters => {
  const active: IntelligenceFilters = {};

  (Object.keys(selections) as IntelligenceFilterId[]).forEach(id => {
    if (selections[id] !== DEFAULT_FILTER_SELECTIONS[id]) {
      active[id] = selections[id];
    }
  });

  return active;
};

// The filter state for the whole screen. Categories combine with AND — every active category is
// carried into the seam's cache key at once — while the options within one category are
// single-select, which is the platform's own semantics for the radio group each card draws.
// Collapsing the panel must not clear a selection, which is why the state lives here and not in the
// panel. PLA-8815.
export const useInsightFilters = (): InsightFiltersValue => {
  const [selections, setSelections] =
    useState<Record<IntelligenceFilterId, string>>(DEFAULT_FILTER_SELECTIONS);
  const [openFilterId, setOpenFilterId] = useState<IntelligenceFilterId | null>(null);

  const toggleFilterOpen = useCallback((id: IntelligenceFilterId) => {
    setOpenFilterId(current => (current === id ? null : id));
  }, []);

  const closeFilter = useCallback(() => setOpenFilterId(null), []);

  const setFilter = useCallback((id: IntelligenceFilterId, value: string) => {
    setSelections(current => ({ ...current, [id]: value }));
  }, []);

  const resetFilter = useCallback((id: IntelligenceFilterId) => {
    setSelections(current => ({ ...current, [id]: DEFAULT_FILTER_SELECTIONS[id] }));
  }, []);

  const clearAll = useCallback(() => setSelections(DEFAULT_FILTER_SELECTIONS), []);

  const filters = useMemo(() => activeFiltersOf(selections), [selections]);
  const dateRange = useMemo(() => resolveDateRange(selections.dateRange), [selections.dateRange]);
  const comparisonRange = useMemo(() => previousRange(dateRange), [dateRange]);

  return {
    selections,
    filters,
    dateRange,
    comparisonRange,
    hasActiveFilters: Object.keys(filters).length > 0,
    openFilterId,
    toggleFilterOpen,
    closeFilter,
    setFilter,
    resetFilter,
    clearAll
  };
};

let inertValue: InsightFiltersValue | undefined;

// What a consumer rendered outside the provider sees: every category on its default, nothing active,
// setters that do nothing. A card mounted in isolation still resolves rather than throwing, and it
// resolves against the same cache key an unfiltered screen produces. Built on first use rather than
// at import so the date range is read from the clock of the session that asks for it.
export const unfilteredInsightFilters = (): InsightFiltersValue => {
  if (!inertValue) {
    const dateRange = resolveDateRange(DEFAULT_FILTER_SELECTIONS.dateRange);

    inertValue = {
      selections: DEFAULT_FILTER_SELECTIONS,
      filters: {},
      dateRange,
      comparisonRange: previousRange(dateRange),
      hasActiveFilters: false,
      openFilterId: null,
      toggleFilterOpen: () => undefined,
      closeFilter: () => undefined,
      setFilter: () => undefined,
      resetFilter: () => undefined,
      clearAll: () => undefined
    };
  }

  return inertValue;
};

export default useInsightFilters;
